import { impersonationLoginApi, impersonationVerifyLoginApi } from "@/api/services/admin/auth";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { RESEND_COOLDOWN_SECONDS, RESEND_IMPERSONATION_VERIFY_CODE_COOLDOWN_STORAGE_KEY } from "@/constants/common";
import { TUserLoginVerifyForm, userLoginVerifySchema } from "@/features/session/schema/userLoginVerifySchema";
import { TUserFirstLoginState } from "@/features/session/types/userFirstLoginState";
import { TUserLoginVerifyState } from "@/features/session/types/userLoginVerifyState";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { getInitialCooldownOnReload } from "@/utils/cooldown";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { openNewTab } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { Stack, SxProps, Theme, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

type TUserLoginVerifyCardProps = {
  sx?: SxProps<Theme>;
};

export const UserLoginVerifyCard: FC<TUserLoginVerifyCardProps> = ({ sx }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const verifyState = location.state as TUserLoginVerifyState | null;

  const [resendCooldown, setResendCooldown] = useState(() => getInitialCooldownOnReload(RESEND_IMPERSONATION_VERIFY_CODE_COOLDOWN_STORAGE_KEY));
  const [isResending, setIsResending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      localStorage.removeItem(RESEND_IMPERSONATION_VERIFY_CODE_COOLDOWN_STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      localStorage.removeItem(RESEND_IMPERSONATION_VERIFY_CODE_COOLDOWN_STORAGE_KEY);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          localStorage.removeItem(RESEND_IMPERSONATION_VERIFY_CODE_COOLDOWN_STORAGE_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendCooldown > 0]);

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0 || isResending || !verifyState?.loginPayload) return;
    setIsResending(true);
    try {
      await impersonationLoginApi(verifyState.loginPayload);
      toast.success("認証コードを再送しました");
      const cooldownUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      localStorage.setItem(RESEND_IMPERSONATION_VERIFY_CODE_COOLDOWN_STORAGE_KEY, String(cooldownUntil));
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      showError(error);
    } finally {
      setIsResending(false);
    }
  }, [verifyState, resendCooldown, isResending]);

  const methods = useForm<TUserLoginVerifyForm>({
    defaultValues: { email: verifyState?.verificationEmail ?? "", code: "" },
    mode: "all",
    reValidateMode: "onBlur",
    resolver: yupResolver(userLoginVerifySchema),
  });
  const { control, formState } = methods;
  const onSubmit = async ({ code }: TUserLoginVerifyForm) => {
    if (!verifyState?.loginPayload) return;

    try {
      const data = await impersonationVerifyLoginApi({ userId: verifyState.loginPayload.userId, code });
      if (data?.requiredUserRegistration) {
        if (!data.userRegistrationToken) return;
        const firstLoginState: TUserFirstLoginState = {
          organizationId: verifyState.organizationId,
          userRegistrationToken: data.userRegistrationToken,
          productCode: verifyState.productCode,
          purposes: verifyState.purposes,
          otherText: verifyState.otherText,
        };
        navigate(AppRoutes.userFirstLogin, { state: firstLoginState, replace: true });
        return;
      }

      openNewTab(data.redirectUrl, `session:${verifyState.productCode}`);
      navigate(AppRoutes.organizationSelect, { replace: true });
    } catch (error) {
      showError(error);
    }
  };

  // Reached directly without a pending login → restart the flow.
  if (!verifyState?.loginPayload) {
    return <Navigate to={AppRoutes.organizationSelect} replace />;
  }

  return (
    <InnerBox
      padding="32px"
      sx={{ gap: 3, ...sx }}
      title={
        <Typography variant="head20Bold" color={text.primary} width="100%" textAlign="center">
          追加の二段階認証
        </Typography>
      }
    >
      <Stack gap="28px">
        <Typography variant="body14" color={text.secondary}>
          セキュリティチェックのため、メールで認証コードを確認し、入力してください。この追加認証は24時間有効です。
        </Typography>
        <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={userLoginVerifySchema}>
          <Stack gap="28px">
            <Stack gap="24px">
              <FormField control={control} name="email" label="メールアドレス" required={false}>
                <Input size="lg" readonly />
              </FormField>
              <FormField control={control} name="code" label="認証コード" required>
                <Input size="lg" />
              </FormField>
            </Stack>
            <Stack gap={2}>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={formState.isSubmitting}
                startIcon={<LoginRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{ height: "48px !important" }}
              >
                <Typography variant="body14Semi">顧客環境にログイン</Typography>
              </Button>
              <Button type="button" size="sm" variant="ghost" fullWidth disabled={resendCooldown > 0 || isResending} onClick={handleResendCode}>
                {resendCooldown > 0 ? `認証コードを再送する（${resendCooldown}秒）` : "認証コードを再送する"}
              </Button>
            </Stack>
          </Stack>
        </Form>
        <Button type="button" variant="ghost" fullWidth onClick={() => navigate(-1)}>
          <Typography variant="body14Semi">戻る</Typography>
        </Button>
      </Stack>
    </InnerBox>
  );
};
